// hooks/useOrderCreation.ts
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getApi } from "../../../../api/api";
import {
  DeliveryService,
  DeliveryType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
} from "../../../../common/types/enums";
import {
  OrderInterface,
  CartItem,
  SaleItemInterface,
  PromoInterface,
} from "../../../../common/types/types";
import { clearCart } from "../../../../store/slices/cartSlice";

import { useCartValidation } from "./useCartValidation";
import { selectCurrentUser } from "../../../../store/slices/currentUserSlice";
import { formatSaleItemName } from "../../../utils/functions/formatSaleItemName";

interface CdekOrderData {
  tariffCode: number;
  pickupPointCode?: string;
  pickupPointName?: string;
  pickupPointAddress?: string;
  pickupPointPhone?: string;
  tariffName?: string;
  periodMin?: number;
  periodMax?: number;
  deliveryDateMin?: string;
  deliveryDateMax?: string;
}

interface UseOrderCreationProps {
  cartItems: CartItem[];
  saleItemList: SaleItemInterface[];
  itemsTotal: number;
  deliveryFee: number;
  discountAmount: number;
  totalWithDelivery: number;
  deliveryType: DeliveryType;
  pickUpStore?: number;
  deliveryStoreId?: number | null;
  selectedAddressId?: number | null;
  paymentMethod: PaymentMethod;
  selectedPromo: PromoInterface | null;
  discountCode: string;
  bonusesToSpend?: number;
  paymentMethodId?: number;
  desiredTime?: string | null;
  getOrderList: () => Promise<void>;
  phone: string;
  comment?: string;
  cutleryCount?: number;
  selectedGiftItemId?: number;
  deliveryService?: DeliveryService;
  cdekOrder?: CdekOrderData;
}

export const useOrderCreation = ({
  cartItems,
  saleItemList,
  itemsTotal,
  deliveryFee,
  discountAmount,
  totalWithDelivery,
  deliveryType,
  pickUpStore,
  deliveryStoreId,
  selectedAddressId,
  paymentMethod,
  selectedPromo,
  discountCode,
  bonusesToSpend = 0,
  paymentMethodId,
  desiredTime,
  getOrderList,
  phone,
  comment,
  cutleryCount,
  selectedGiftItemId,
  deliveryService,
  cdekOrder,
}: UseOrderCreationProps) => {
  const dispatch = useDispatch();
  const [api] = useState(getApi());
  const currentUser = useSelector(selectCurrentUser);

  const { roundMoney, isPromoApplicable } = useCartValidation();

  const createOrder = useCallback(async () => {
    if (selectedPromo) {
      const storeId =
        deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined;
      const isValid = isPromoApplicable(
        selectedPromo,
        cartItems,
        storeId,
        discountCode,
        deliveryType,
      );
      if (!isValid) {
        throw new Error(
          "Выбранная промо-акция не может быть применена к этому заказу",
        );
      }
    }

    let storeId: number | undefined;
    if (deliveryType === DeliveryType.PICKUP) {
      storeId = pickUpStore;
    } else if (deliveryType === DeliveryType.DELIVERY && deliveryStoreId) {
      storeId = deliveryStoreId;
    }

    const finalComment =
      cutleryCount && cutleryCount > 0
        ? [comment, `Приборы: ${cutleryCount}`].filter(Boolean).join("\n")
        : comment || "";

    const newOrder: OrderInterface = {
      status: OrderStatus.NEW,
      deliveryType,
      fullAddressId: selectedAddressId || undefined,
      paymentMethod,
      paymentStatus: PaymentStatus.PENDING,
      isPaid: false,
      items: cartItems.map((item) => {
        const saleItem = saleItemList.find((el) => el.id === Number(item.id));
        return {
          itemId: Number(item.id),
          name: saleItem
            ? formatSaleItemName(saleItem, 4)
            : item.name || `Товар #${item.id}`,
          price: item.price,
          quantity: item.quantity,
          total: roundMoney(item.price * item.quantity),
          categoryId: item.categoryId,
          unit: item.unit,
        };
      }),
      subtotal: itemsTotal,
      deliveryFee: deliveryFee,
      total: totalWithDelivery,
      storeId: storeId,
      desiredTime: desiredTime || undefined,
      orderNumber: "",
      promoId: selectedPromo?.id,
      discountCode: discountCode || undefined,
      discountAmount: discountAmount,
      bonusesSpent: bonusesToSpend,
      phone_number: phone,
      description: finalComment,
      paymentMethodId,
      selectedGiftItemId,
      deliveryService,
      cdekOrder,
    };

    const result = await api.createOrder(newOrder);
    await getOrderList();
    dispatch(clearCart());

    return result;
  }, [
    cartItems,
    saleItemList,
    itemsTotal,
    deliveryFee,
    discountAmount,
    totalWithDelivery,
    deliveryType,
    pickUpStore,
    deliveryStoreId,
    selectedAddressId,
    paymentMethod,
    selectedPromo,
    discountCode,
    bonusesToSpend,
    paymentMethodId,
    desiredTime,
    phone,
    comment,
    cutleryCount,
    selectedGiftItemId,
    deliveryService,
    cdekOrder,
    api,
    dispatch,
    roundMoney,
    isPromoApplicable,
    getOrderList,
  ]);

  return {
    createOrder,
  };
};
